import React, { useState } from 'react';
import { Card, Table, Button, Tag, Space, Tooltip, Spin, Alert, Typography } from 'antd';
import { EditOutlined, HistoryOutlined, DollarOutlined, InfoCircleOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useProducts } from '../../../hooks/useProducts';
import { Product } from '../../../services/product/product.service';
import UpdatePriceModal from '../../shared/modals/UpdatePriceModal';
import { formatDate } from '../../../utils/date';

const { Title, Text } = Typography;

const ProductManagement: React.FC = () => {
  const { products, loading, error, updateProductPrice } = useProducts();
  const [updateModalVisible, setUpdateModalVisible] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);

  const handleUpdatePrice = async (productId: number, pricePerUserPerMonth: number) => {
    await updateProductPrice(productId, { pricePerUserPerMonth });
  };

  const openUpdateModal = (product: Product) => {
    setSelectedProduct(product);
    setUpdateModalVisible(true);
  };

  const getDurationText = (durationMonths: number) => {
    return `${durationMonths} Aylık Paket`;
  };

  const getDurationColor = (durationMonths: number) => {
    if (durationMonths <= 3) return 'blue';
    if (durationMonths <= 6) return 'green';
    return 'purple';
  };

  const columns: ColumnsType<Product> = [
    {
      title: 'Paket Süresi',
      dataIndex: 'durationMonths',
      key: 'durationMonths',
      render: (durationMonths: number) => (
        <Tag color={getDurationColor(durationMonths)} className="text-sm">
          {getDurationText(durationMonths)}
        </Tag>
      ),
    },
    {
      title: 'Paket Adı',
      dataIndex: 'name',
      key: 'name',
      render: (name: string) => <Text strong>{name}</Text>,
    },
    {
      title: 'Kullanıcı Başına Aylık Ücret',
      dataIndex: 'pricePerUserPerMonth',
      key: 'pricePerUserPerMonth',
      render: (pricePerUserPerMonth: number) => (
        <Text className="text-lg font-semibold text-green-600">
          ₺{pricePerUserPerMonth}/kullanıcı/ay
        </Text>
      ),
      sorter: (a, b) => a.pricePerUserPerMonth - b.pricePerUserPerMonth,
    },
    {
      title: 'Durum',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'success' : 'default'}>
          {isActive ? 'Aktif' : 'Pasif'}
        </Tag>
      ),
      filters: [
        { text: 'Aktif', value: true },
        { text: 'Pasif', value: false },
      ],
      onFilter: (value, record) => record.isActive === value,
    },
    {
      title: 'Açıklama',
      dataIndex: 'description',
      key: 'description',
      render: (description: string) => (
        <Text type="secondary" className="max-w-xs truncate">
          {description || '-'}
        </Text>
      ),
    },
    {
      title: 'Oluşturulma Tarihi',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => formatDate(date),
      sorter: (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_, record: Product) => (
        <Space>
          {record.isActive && (
            <Tooltip title="Fiyatı Güncelle">
              <Button
                type="primary"
                icon={<EditOutlined />}
                size="small"
                onClick={() => openUpdateModal(record)}
              >
                Fiyat Güncelle
              </Button>
            </Tooltip>
          )}
          {!record.isActive && (
            <Tooltip title="Bu kayıt geçmiş fiyat bilgisidir">
              <Tag color="orange" icon={<HistoryOutlined />}>
                Geçmiş Kayıt
              </Tag>
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  if (error) {
    return (
        <div className="page-wrapper vh-100 d-flex flex-column justify-content-between">
        <div className="content flex-fill h-100">
      <div className="p-6">
        <Alert
          message="Hata"
          description={error}
          type="error"
          showIcon
          action={
            <Button size="small" danger>
              Tekrar Dene
            </Button>
          }
        />
      </div>
      </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper vh-100 d-flex flex-column justify-content-between">
        <div className="content flex-fill h-100">
    <div className="p-6">
      <div className="mb-6">
        <Title level={2} className="mb-2">
          <DollarOutlined className="mr-2" />
          Ürün ve Fiyat Yönetimi
        </Title>
        <Text type="secondary">
          Abonelik planlarının fiyatlarını yönetin ve geçmiş fiyat değişikliklerini görüntüleyin.
        </Text>
      </div>

      {/* Aktif Planlar Özeti */}
      {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {activeProducts.map((product) => (
          <Card
            key={product.id}
            className="border-l-4"
            style={{
              borderLeftColor: product.planType === 'monthly' ? '#1890ff' : '#52c41a'
            }}
          >
            <div className="flex justify-between items-start">
              <div>
                <Title level={4} className="mb-1">
                  {getPlanTypeName(product.planType)}
                </Title>
                <Text type="secondary" className="block mb-2">
                  {product.name}
                </Text>
                <Text className="text-2xl font-bold text-green-600">
                  {product.price}
                </Text>
              </div>
              <Button
                type="primary"
                icon={<EditOutlined />}
                onClick={() => openUpdateModal(product)}
              >
                Güncelle
              </Button>
            </div>
          </Card>
        ))}
      </div> */}

      {/* Bilgilendirme */}
      <Alert
        message="Fiyat Güncelleme Hakkında"
        description="Bir planın fiyatı güncellendiğinde, eski kayıt pasif hale getirilir ve yeni fiyat ile yeni bir kayıt oluşturulur. Bu sayede tüm fiyat geçmişi korunur."
        type="info"
        icon={<InfoCircleOutlined />}
        className="mb-6 mt-6"
        showIcon
      />

      {/* Tüm Ürünler Tablosu */}

      <Card title="Tüm Ürün Kayıtları" className="shadow-sm">
        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={products}
            rowKey="id"
            pagination={{
              pageSize: 10,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} / ${total} kayıt`,
            }}
            scroll={{ x: 800 }}
            className="ant-table-responsive"
          />
        </Spin>
      </Card>

      {/* Fiyat Güncelleme Modalı */}
              <UpdatePriceModal
          visible={updateModalVisible}
          onClose={() => {
            setUpdateModalVisible(false);
            setSelectedProduct(null);
          }}
          onUpdate={(pricePerUserPerMonth: number) => 
            selectedProduct && handleUpdatePrice(selectedProduct.id, pricePerUserPerMonth)
          }
          product={selectedProduct}
          loading={loading}
          />
      </div>
    </div>
    </div>
  );
};

export default ProductManagement; 